import React from 'react';
import Icon from '../components/Icon';
import { currencyLabel, formatMoney } from './format';
import './billing.css';

// Monthly / yearly switch. Yearly shows its per-month price, so the two compare directly.
export const IntervalToggle = ({ interval, onChange, discount, dark }) => (
    <div className={`ls-interval${dark ? ' is-dark' : ''}`} role="radiogroup" aria-label="Billing period">
        {[['month', 'Monthly'], ['year', 'Yearly']].map(([id, label]) => (
            <button
                type="button"
                key={id}
                role="radio"
                aria-checked={interval === id}
                className={interval === id ? 'is-active' : ''}
                onClick={() => onChange(id)}
            >
                {label}
                {id === 'year' && discount > 0 && <span className="ls-save">Save {discount}%</span>}
            </button>
        ))}
    </div>
);

export const CurrencySelect = ({ currency, currencies = [], onChange, dark, disabled }) => (
    <label className={`ls-currency${dark ? ' is-dark' : ''}`}>
        <span className="ls-sr-only">Currency</span>
        <select value={currency} onChange={(e) => onChange(e.target.value)} disabled={disabled} title={disabled ? 'Your plan is billed in this currency' : 'Currency'}>
            {currencies.map(code => <option key={code} value={code}>{currencyLabel(code)}</option>)}
        </select>
    </label>
);

export const PlanPrice = ({ plan, interval, currency }) => {
    if (plan.price.month === 0) {
        return (
            <div className="ls-price">
                <div className="ls-price-line">
                    <span className="ls-price-amount">{formatMoney(0, currency)}</span>
                    <span className="ls-price-unit">/ month</span>
                </div>
                <div className="ls-price-note">Free forever, no card needed</div>
            </div>
        );
    }
    const yearly = interval === 'year';
    const saving = plan.price.month * 12 - plan.price.year;
    return (
        <div className="ls-price">
            <div className="ls-price-line">
                <span className="ls-price-amount">{formatMoney(yearly ? plan.price.yearPerMonth : plan.price.month, currency)}</span>
                <span className="ls-price-unit">/ month</span>
            </div>
            <div className="ls-price-note">
                {yearly
                    ? <>Billed {formatMoney(plan.price.year, currency)} a year{saving > 0 && <span className="ls-price-save"> · save {formatMoney(saving, currency)}</span>}</>
                    : 'Billed monthly'}
            </div>
        </div>
    );
};

// What the button on each card says and does. `current` renders a label instead of a button.
const planAction = ({ plan, interval, account, mode }) => {
    if (mode === 'waitlist') return { label: 'Join the waitlist' };
    const current = account?.subscribedPlan;
    if (plan.id === 'free') {
        if (mode === 'landing') return { label: 'Start free' };
        if (!current) return { label: 'Your current plan', current: true };
        return { label: 'Where you land if you cancel', quiet: true };
    }
    if (current === plan.id && account?.interval === interval) return { label: 'Your current plan', current: true };
    if (current === plan.id) return { label: interval === 'year' ? 'Switch to yearly billing' : 'Switch to monthly billing' };
    if (current) return { label: `Switch to ${plan.name}` };
    const trial = plan.trialDays > 0 && (mode === 'landing' || account?.trialAvailable !== false);
    return { label: trial ? `Start ${plan.trialDays}-day free trial` : `Get ${plan.name}`, trial };
};

const PlanCard = ({ plan, interval, currency, account, mode, onSelect, busy, isHighlighted }) => {
    const action = planAction({ plan, interval, account, mode });
    const isBusy = busy === `plan:${plan.id}`;
    const fine = action.trial ? `No charge for ${plan.trialDays} days. Cancel anytime.` : plan.price.month > 0 && !action.current ? 'Cancel anytime.' : '';
    return (
        <div className={`ls-plan${isHighlighted ? ' is-highlighted' : ''}`}>
            {isHighlighted && <div className="ls-plan-badge">{plan.highlight || 'Recommended'}</div>}
            <div className="ls-plan-name">{plan.name}</div>
            <div className="ls-plan-outcome">{plan.outcome}</div>
            <PlanPrice plan={plan} interval={interval} currency={currency} />
            {action.current || action.quiet ? (
                <div className={`ls-plan-status${action.current ? ' is-current' : ' is-quiet'}`}>
                    {action.current && <Icon name="check_circle" size={18} />}{action.label}
                </div>
            ) : (
                <button
                    type="button"
                    className={`ls-btn ${isHighlighted ? 'ls-btn-primary' : 'ls-btn-secondary'} ls-plan-cta`}
                    disabled={Boolean(busy)}
                    onClick={() => onSelect(plan.id, interval)}
                >
                    {isBusy ? <span className="ls-spinner" /> : action.label}
                </button>
            )}
            <div className="ls-plan-fine">{fine}</div>
            <ul className="ls-plan-features">
                {plan.features.map(feature => (
                    <li key={feature}><Icon name="check" size={18} />{feature}</li>
                ))}
            </ul>
        </div>
    );
};

// Three plans side by side, one highlighted.
// mode "landing": everyone is a visitor; "waitlist": visitors, and every button
// joins the waitlist; "app": buttons follow the user's account.
const PricingTable = ({
    pricing, interval, onIntervalChange, onCurrencyChange, onSelect, account, mode = 'app', busy = '', plans, recommended, dark,
}) => {
    if (!pricing?.plans) {
        return (
            <div className="ls-plans">
                {[0, 1, 2].map(i => <div key={i} className="ls-skeleton" style={{ height: 420, borderRadius: 16 }} />)}
            </div>
        );
    }
    const shown = plans ? pricing.plans.filter(plan => plans.includes(plan.id)) : pricing.plans;
    // One plan stands out: the one we recommend for this moment, else the most popular
    const highlighted = recommended || pricing.plans.find(plan => plan.highlight)?.id;
    return (
        <div className="ls-pricing">
            <div className="ls-pricing-controls">
                <IntervalToggle interval={interval} onChange={onIntervalChange} discount={pricing.yearlyDiscount} dark={dark} />
                {onCurrencyChange && (
                    <CurrencySelect
                        currency={pricing.currency}
                        currencies={pricing.currencies}
                        onChange={onCurrencyChange}
                        dark={dark}
                        disabled={Boolean(account?.currency)}
                    />
                )}
            </div>
            <div className={`ls-plans is-${shown.length}`}>
                {shown.map(plan => (
                    <PlanCard
                        key={plan.id}
                        plan={plan}
                        interval={interval}
                        currency={pricing.currency}
                        account={account}
                        mode={mode}
                        onSelect={onSelect}
                        busy={busy}
                        isHighlighted={plan.id === highlighted}
                    />
                ))}
            </div>
        </div>
    );
};

export default PricingTable;
